import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiUpload, FiSave, FiX, FiAlertCircle, FiCheckCircle, FiImage } from "react-icons/fi";
import { useParams } from "react-router-dom";
import { getApiBase } from "../apiBase";
import { HiOutlineClipboardCopy, HiCheck } from "react-icons/hi";  
import { FaCopy } from "react-icons/fa";

const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0, y: -50 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      className={`fixed top-4 right-4 z-50 flex items-center gap-3 px-5 py-4 rounded-xl shadow-lg max-w-md ${
        type === "success"
          ? "bg-gradient-to-r from-[#2E7D32] to-[#1B5E20] text-white"
          : "bg-gradient-to-r from-[#C62828] to-[#B71C1C] text-white"
      }`}
      role="alert"
      aria-live="assertive"
    >
      {type === "success" ? (
        <FiCheckCircle className="w-6 h-6" />
      ) : (
        <FiAlertCircle className="w-6 h-6" />
      )}
      <span className="font-medium">{message}</span>
      <button
        onClick={onClose}
        className="ml-auto hover:opacity-80 transition-opacity"
        aria-label="Close notification"
      >
        <FiX />
      </button>
    </motion.div>
  );
};

const ImagePreview = ({ src, alt, onRemove }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  
  useEffect(() => {
    setImageLoaded(false);
  }, [src]);

  return (
    <div className="relative group">
      <img
        src={src}
        alt={alt}
        className={`h-24 w-24 rounded-xl object-contain ${
          imageLoaded
            ? "border-2 border-[#B89D4F]/50"
            : "border-2 border-dashed border-[#A89CA5]/40 bg-gray-50"
        }`}
        onLoad={() => setImageLoaded(true)}
        onError={() => setImageLoaded(false)}
      />

      {imageLoaded && (
        <button
          onClick={onRemove}
          className="absolute -top-2 -right-2 p-1 bg-[#5B3A60] text-white rounded-full shadow-md hover:bg-[#7B5E57] transition-colors opacity-0 group-hover:opacity-100"
        >
          <FiX className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

export default function BrandingSettings() {
  const { tenantId, botId } = useParams();
  const embedCode = `<script src="${getApiBase()}/embed/${tenantId}/${botId}.js"></script>`;
  
  const handleCopyEmbed = async () => {
  try {
    await navigator.clipboard.writeText(embedCode);
    setCopied(true);

    setTimeout(() => {
      setCopied(false);
    }, 2000);

    setToast({
      type: "success",
      message: "Embed code copied successfully!"
    });
  } catch {
    setToast({
      type: "error",
      message: "Failed to copy embed code."
    });
  }
};
  
  const fileInputRef = useRef(null);
  const [toast, setToast] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [brandingExists, setBrandingExists] = useState(false);
  const [copied, setCopied] = useState(false);
  
  const [formData, setFormData] = useState({
    logo: null,
    logoPreview: null,
    companyName: "",
    subtitle: "",
    questions: [""],
  });
  
  const [initialData, setInitialData] = useState(null);
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState(null);

  const MAX_FILE_SIZE = 1 * 1024 * 1024;
  const ALLOWED_FORMATS = ["image/png", "image/jpeg"];
  const ALLOWED_EXTENSIONS = [".png", ".jpg", ".jpeg"];

  useEffect(() => {
    return () => {
      if (formData.logoPreview?.startsWith('blob:')) {
        URL.revokeObjectURL(formData.logoPreview);
      }
    };
  }, [formData.logoPreview]);

  useEffect(() => {
    const fetchBrandingData = async () => {
      try {
        const response = await fetch(
          `${getApiBase()}/tenant/${tenantId}/bot/${botId}/branding`
        );

        if (response.status === 404) {
          setBrandingExists(false);
          setIsInitialized(true);
          return;
        }

        if (!response.ok) throw new Error("Failed to load branding");

        const data = await response.json();
        setBrandingExists(true);

        const savedLogoUrl = `${getApiBase()}/tenant/${tenantId}/bot/${botId}/logo?t=${Date.now()}`;
        
        const initialQuestions = data.welcome_questions?.length ? data.welcome_questions : [""];
        
        setFormData({
          logo: null,
          logoPreview: data.has_logo ? savedLogoUrl : null,
          companyName: data.company_name || "",
          subtitle: data.welcome_subtitle || "",
          questions: initialQuestions,
        });
        
        setInitialData({
          companyName: data.company_name || "",
          subtitle: data.welcome_subtitle || "",
          questions: initialQuestions,
          logoPreview: data.has_logo ? savedLogoUrl : null,
        });

      } catch (err) {
        setServerError("Failed to load branding settings.");
        console.error(err);
      } finally {
        setIsInitialized(true);
      }
    };

    if (tenantId && botId) {
      fetchBrandingData();
    } else {
      setIsInitialized(true);
    }
  }, [tenantId, botId]);

  const validateLogo = (file) => {
    if (!file) {
      setToast({
        type: "error",
        message: "No file selected"
      });
      return false;
    }

    const fileName = file.name.toLowerCase();
    const fileExtension = "." + fileName.split(".").pop();
    
    if (!ALLOWED_EXTENSIONS.includes(fileExtension)) {
      setToast({
        type: "error",
        message: "Only PNG and JPG files are allowed"
      });
      return false;
    }

    if (!ALLOWED_FORMATS.includes(file.type)) {
      setToast({
        type: "error",
        message: "Only PNG and JPG files are allowed"
      });
      return false;
    }

    if (file.size === 0) {
      setToast({
        type: "error",
        message: "File is empty"
      });
      return false;
    }

    if (file.size > MAX_FILE_SIZE) {
      setToast({
        type: "error",
        message: "Logo must be under 1MB"
      });
      return false;
    }

    return true;
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.companyName.trim()) {
      newErrors.companyName = "Company name is required";
    }

    formData.questions.forEach((q, index) => {
      if (q.trim().length > 80) {
        newErrors[`question${index}`] = "Question must be under 80 characters";
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!validateLogo(file)) {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }
    
    if (formData.logoPreview?.startsWith('blob:')) {
      URL.revokeObjectURL(formData.logoPreview);
    }
    
    setFormData(prev => ({
      ...prev,
      logo: file,
      logoPreview: URL.createObjectURL(file)
    }));
    
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemoveLogo = async () => {
    try {
      if (brandingExists) {
        const response = await fetch(
          `${getApiBase()}/tenant/${tenantId}/bot/${botId}/logo`,
          {
            method: "DELETE"
          }
        );

        if (!response.ok) {
          throw new Error("Failed to delete logo");
        }
      }

      if (formData.logoPreview?.startsWith('blob:')) {
        URL.revokeObjectURL(formData.logoPreview);
      }

      setFormData(prev => ({
        ...prev,
        logo: null,
        logoPreview: null
      }));

      setToast({
        type: "success",
        message: "Logo removed successfully!"
      });

    } catch (err) {
      setToast({
        type: "error",
        message: "Failed to delete logo"
      });
    }
  };

  const handleTextChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
    if (field === "questions") {
      value.forEach((q, index) => {
        if (errors[`question${index}`]) {
          setErrors(prev => ({ ...prev, [`question${index}`]: undefined }));
        }
      });
    }
  };

  const handleAddQuestion = () => {
    setFormData(prev => ({
      ...prev,
      questions: [...prev.questions, ""]
    }));
  };

  const handleRemoveQuestion = (index) => {
    const updated = formData.questions.filter((_, i) => i !== index);
    setFormData(prev => ({
      ...prev,
      questions: updated.length ? updated : [""]
    }));
  };

  const handleQuestionChange = (index, value) => {
    const newQuestions = [...formData.questions];
    newQuestions[index] = value;
    handleTextChange("questions", newQuestions);
  };

  const handleSave = async () => {
    if (!validateForm()) {
      setToast({
        type: "error",
        message: "Please fill all the required fields"
      });
      return;
    }
    
    setIsSaving(true);
    setServerError(null);
    
    try {
      const formDataToSend = new FormData();
      formDataToSend.append("company_name", formData.companyName.trim());
      formDataToSend.append("subtitle", formData.subtitle.trim());
      formDataToSend.append("questions", JSON.stringify(
       formData.questions.map(q => q.trim()).filter(q => q)
      ));

      if (formData.logo) {
        formDataToSend.append("logo", formData.logo);
      }
      
      const response = await fetch(
        `${getApiBase()}/tenant/${tenantId}/bot/${botId}/branding`,
        {
          method: brandingExists ? "PUT" : "POST",
          body: formDataToSend
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Save failed");
      }
      
      setToast({
        type: "success",
        message: "Branding settings saved successfully!"
      });
      
      setBrandingExists(true);
      
      const newLogoPreview = formData.logo 
        ? `${getApiBase()}/tenant/${tenantId}/bot/${botId}/logo?t=${Date.now()}`
        : initialData?.logoPreview || null;
      
      setInitialData({
        companyName: formData.companyName,
        subtitle: formData.subtitle,
        questions: formData.questions,
        logoPreview: newLogoPreview
      });

      setFormData(prev => ({
        ...prev,
        logo: null
      }));

    } catch (err) {
      console.error("Save error:", err);
      setServerError(err.message || "Failed to save branding settings. Please try again.");
      setToast({
        type: "error",
        message: "Save failed. Please try again."
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (!isInitialized) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#B89D4F] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[#5B3A60]">Loading branding settings...</p>
        </div>
      </div>
    );
  }

  const hasChanges = () => {
    if (!initialData) return false;

    const questionsChanged = JSON.stringify(formData.questions) !== JSON.stringify(initialData.questions);
    const logoChanged = formData.logo !== null;
    
    return (
      formData.companyName !== initialData.companyName ||
      formData.subtitle !== initialData.subtitle ||
      questionsChanged ||
      logoChanged
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-[#FBF9F7] text-[#5B3A60]">
      <AnimatePresence>
        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}
      </AnimatePresence>

      <header className="sticky top-0 z-40 px-6 py-5 md:px-10 md:py-6 border-b-2 border-[#A89CA5]/30 bg-white/95 backdrop-blur-md shadow-sm">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-[#5B3A60] to-[#7B5E57]">
              Workspace Branding
            </h1>
            <p className="text-sm text-[#6B7280] mt-1 max-w-2xl">
              Customize how your assistants appear to users. Changes apply instantly across all your workspaces.
            </p>
          </div>
          <motion.button
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleSave}
            disabled={isSaving || !hasChanges()}
            className={`mt-4 md:mt-0 px-6 py-3 rounded-xl font-semibold flex items-center gap-2 transition-all ${
              isSaving || !hasChanges()
                ? "bg-[#A89CA5] cursor-not-allowed"
                : "bg-gradient-to-r from-[#5B3A60] to-[#7B5E57] text-white hover:from-[#6B4A70] hover:to-[#8B6E67] shadow-md hover:shadow-lg"
            }`}
            aria-busy={isSaving}
          >
            {isSaving ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <FiSave className="w-5 h-5" />
                Save Changes
              </>
            )}
          </motion.button>
        </div>
      </header>

      <main className="px-4 py-8 md:px-10 max-w-5xl mx-auto">
        {serverError && (
          <div className="mb-8 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded-r-lg flex items-start gap-3">
            <FiAlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
            <p className="text-sm">{serverError}</p>
          </div>
        )}

        <section className="mb-12 p-6 bg-white rounded-2xl border-2 border-[#B89D4F]/30 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-5">
            <div>
              <h2 className="text-xl font-bold flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-[#B89D4F]/10 flex items-center justify-center">
                  <FiImage className="text-[#B89D4F]" />
                </span>
                Workspace Logo
              </h2>
              <p className="text-sm text-[#6B7280] mt-1 max-w-2xl">
                Recommended size: 400x400px (max 2MB). PNG, JPG or WebP formats only. Transparent backgrounds work best.
              </p>
            </div>
            <div className="mt-4 md:mt-0 flex flex-wrap gap-3">
              <label className="cursor-pointer group">
                <div className="px-5 py-2.5 bg-white border-2 border-[#5B3A60] text-[#5B3A60] rounded-xl hover:bg-[#5B3A60] hover:text-white transition-all flex items-center gap-2 shadow-sm hover:shadow-md">
                  <FiUpload className="group-hover:animate-bounce" />
                  <span>Upload Logo</span>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".png,.jpg,.jpeg,image/png,image/jpeg"
                  onChange={handleLogoChange}
                  className="hidden"
                  aria-label="Upload logo image"
                />
              </label>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-8">
            {formData.logoPreview && (
              <ImagePreview
                src={formData.logoPreview}
                alt="Logo preview"
                onRemove={handleRemoveLogo}
              />
            )}
            {errors.logo && (
              <p className="text-red-500 text-sm mt-2 flex items-center gap-1">
                <FiAlertCircle className="w-4 h-4" />
                {errors.logo}
              </p>
            )}
          </div>
        </section>

        <div className="border-t-2 border-[#A89CA5]/30 my-10" />

        <div className="space-y-10">
          <section className="p-6 bg-white rounded-2xl border-2 border-[#B89D4F]/30 shadow-sm">
            <label htmlFor="company-name" className="block mb-3">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-[#5B3A60]/10 flex items-center justify-center text-sm">
                  A
                </span>
                Company Display Name
              </h2>
              <p className="text-sm text-[#6B7280] mt-1">This appears as your workspace name across all interfaces</p>
            </label>
            <div className="relative">
              <input
                id="company-name"
                type="text"
                value={formData.companyName}
                onChange={(e) => handleTextChange("companyName", e.target.value)}
                placeholder="ABC Education Consultancy"
                maxLength="100"
                className={`w-full px-5 py-4 bg-white border-2 ${
                  errors.companyName
                    ? "border-red-300 focus:ring-red-200"
                    : "border-[#A89CA5]/30 focus:ring-[#B89D4F]/30"
                } rounded-xl focus:outline-none focus:ring-2 transition-all`}
                aria-invalid={!!errors.companyName}
                aria-describedby={errors.companyName ? "company-name-error" : undefined}
              />
              <div className="absolute right-4 top-1/2 -translate-y-1/2 text-xs text-[#A89CA5]">
                {formData.companyName.length}/100
              </div>
            </div>
            {errors.companyName && (
              <p id="company-name-error" className="text-red-500 text-sm mt-2 flex items-center gap-1">
                <FiAlertCircle className="w-4 h-4" />
                {errors.companyName}
              </p>
            )}
          </section>

          <div className="border-t-2 border-[#A89CA5]/30 my-10" />

          <section className="p-6 bg-white rounded-2xl border-2 border-[#B89D4F]/30 shadow-sm">
            <label htmlFor="subtitle" className="block mb-3">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-[#B89D4F]/10 flex items-center justify-center text-sm">
                  B
                </span>
                Welcome Subtitle
              </h2>
              <p className="text-sm text-[#6B7280] mt-1">A short description that appears below your company name</p>
            </label>
            <input
              id="subtitle"
              type="text"
              value={formData.subtitle}
              onChange={(e) => handleTextChange("subtitle", e.target.value)}
              placeholder="Your trusted study abroad partner"
              maxLength="80"
              className="w-full px-5 py-4 bg-white border-2 border-[#A89CA5]/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#B89D4F]/30 transition-all"
            />
            <p className="text-xs text-[#A89CA5] text-right mt-1">
              {formData.subtitle.length}/80
            </p>
            {errors.subtitle && (
              <p className="text-red-500 text-sm mt-2 flex items-center gap-1">
                <FiAlertCircle className="w-4 h-4" />
                {errors.subtitle}
              </p>
            )}
          </section>

          <div className="border-t-2 border-[#A89CA5]/30 my-10" />

          <section className="p-6 bg-white rounded-2xl border-2 border-[#B89D4F]/30 shadow-sm">
            <div className="mb-5">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-[#7B5E57]/10 flex items-center justify-center text-sm">
                  ?
                </span>
                Welcome Questions
              </h2>
              <p className="text-sm text-[#6B7280] mt-1">
                Suggested questions users can ask when they first interact with your assistant. Leave blank to skip.
              </p>
              {errors.questions && (
                <p className="text-red-500 text-sm mt-2 flex items-center gap-1">
                  <FiAlertCircle className="w-4 h-4" />
                  {errors.questions}
                </p>
              )}
            </div>

            <div className="space-y-4">
              {formData.questions.map((q, index) => (
                <div key={index} className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#A89CA5]/10 flex items-center justify-center font-bold text-[#5B3A60] text-sm">
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <input
                      type="text"
                      value={q}
                      onChange={(e) => handleQuestionChange(index, e.target.value)}
                      placeholder='e.g., "How do I apply for scholarships?"'
                      maxLength="80"
                      className="w-full px-5 py-4 bg-white border-2 border-[#A89CA5]/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#B89D4F]/30 transition-all"
                    />
                    <div className="flex justify-between mt-1">
                      <span className="text-xs text-[#A89CA5]">
                        {q.length}/80
                      </span>
                      {formData.questions.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveQuestion(index)}
                          className="text-red-500 text-xs hover:underline"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6">
              <button
                type="button"
                onClick={handleAddQuestion}
                className="px-4 py-2 border-2 border-dashed border-[#5B3A60] text-[#5B3A60] rounded-xl hover:bg-[#5B3A60] hover:text-white transition-all"
              >
                + Add Question
              </button>
            </div>
          </section>

          <section className="mt-12 p-6 bg-white rounded-2xl border-2 border-[#B89D4F]/30 shadow-sm">
            <div className="mb-5">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-[#5B3A60]/10 flex items-center justify-center">
                  {"</>"}
                </span>
                Embed on Your Website
              </h2>
              <p className="text-sm text-[#6B7280] mt-1">
                Copy and paste this code before the closing {"</body>"} tag on your website.
              </p>
            </div>

            <div className="relative">
  <textarea
    value={embedCode}
    readOnly
    className="w-full h-24 px-4 py-4 pr-24 bg-gray-50 border-2 border-[#A89CA5]/30 rounded-xl text-sm font-mono focus:outline-none"
  />

  <div className="absolute top-1/2 right-3 -translate-y-1/2 group">
  <button
    onClick={handleCopyEmbed}
    className="px-3 py-1.5 bg-white border border-[#A89CA5]/40 rounded-lg text-xs font-medium text-[#5B3A60] hover:bg-[#F5F3F1] transition flex items-center gap-1 shadow-sm"
  >
    {copied ? (
      <HiCheck className="w-4 h-4 text-green-600" />
    ) : (
      <FaCopy size={12} />
    )}
  </button>

  <div className="absolute bottom-full right-0 mb-2 px-2 py-1 text-[11px] text-white bg-black/70 rounded opacity-0 group-hover:opacity-100 transition pointer-events-none whitespace-nowrap">
    {copied ? "Copied!" : "Copy code"}
  </div>
</div>
</div>
          </section>
        </div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t-2 border-[#A89CA5]/30 shadow-lg md:hidden z-40"
        >
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleSave}
            disabled={isSaving || !hasChanges()}
            className={`w-full px-6 py-4 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all ${
              isSaving || !hasChanges()
                ? "bg-[#A89CA5] cursor-not-allowed"
                : "bg-gradient-to-r from-[#5B3A60] to-[#7B5E57] text-white hover:from-[#6B4A70] hover:to-[#8B6E67] shadow-md"
            }`}
            aria-busy={isSaving}
          >
            {isSaving ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Saving Changes...
              </>
            ) : (
              <>
                <FiSave className="w-5 h-5" />
                Save Changes
              </>
            )}
          </motion.button>
        </motion.div>
      </main>

      <footer className="py-6 text-center text-sm text-[#A89CA5] border-t-2 border-[#A89CA5]/30 mt-12">
        <p>Branding updates apply instantly • All changes are versioned and reversible</p>
      </footer>
    </div>
  );
}